import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { AdminPage } from "@/components/AdminPage";
import { Sparkline } from "@/components/Sparkline";
import { DeltaBadge } from "@/components/DeltaBadge";
import {
  PeriodSwitcher,
  type PeriodKey,
} from "@/components/PeriodSwitcher";
import { api } from "@/lib/api";
import type { ApiContract } from "@podecontratar/shared/contract/index";
import type { EndpointOutput } from "@podecontratar/shared/contract/endpoint";

type Overview = EndpointOutput<ApiContract["GET /admin/overview"]>;

function formatBRL(cents: number): string {
  return (cents / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function formatInt(n: number): string {
  return n.toLocaleString("pt-BR");
}

const CARDS: Array<{
  key: string;
  label: string;
  format: (n: number) => string;
  highlight?: boolean;
}> = [
  { key: "users", label: "Usuários cadastrados", format: formatInt },
  { key: "entities", label: "Entidades criadas", format: formatInt },
  { key: "verifications", label: "Consultas iniciadas", format: formatInt },
  { key: "paidVerifications", label: "Pagas / em andamento", format: formatInt },
  { key: "completedVerifications", label: "Concluídas", format: formatInt },
  {
    key: "grossRevenueCents",
    label: "Receita bruta",
    format: formatBRL,
    highlight: true,
  },
];

export default function AdminOverviewPage() {
  const [period, setPeriod] = useState<PeriodKey>("30d");
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setData(null);
    setError(false);
    api
      .request("GET /admin/overview", { query: { period } })
      .then((d) => {
        if (alive) setData(d);
      })
      .catch(() => {
        if (alive) setError(true);
      });
    return () => {
      alive = false;
    };
  }, [period]);

  return (
    <AdminPage
      eyebrow="Visão geral"
      title="Admin"
      description="Métricas agregadas. Variação compara o período atual com o período imediatamente anterior."
      action={<PeriodSwitcher value={period} onChange={setPeriod} />}
    >
      {error ? (
        <div className="rounded-3xl border border-danger/30 bg-danger/10 p-5 text-sm text-text">
          Não foi possível carregar.
        </div>
      ) : data === null ? (
        <div className="grid place-items-center rounded-3xl border border-border bg-surface py-20">
          <Loader2 size={20} className="animate-spin text-text-tertiary" aria-hidden />
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {CARDS.map((card) => {
            const m = data.metrics[card.key];
            if (!m) return null;
            return (
              <Card
                key={card.key}
                label={card.label}
                value={card.format(m.current)}
                deltaPct={m.delta_pct}
                series={m.series.map((p) => p.v)}
                highlight={card.highlight}
              />
            );
          })}
        </div>
      )}
    </AdminPage>
  );
}

function Card({
  label,
  value,
  deltaPct,
  series,
  highlight,
}: {
  label: string;
  value: string;
  deltaPct: number | null;
  series: number[];
  highlight?: boolean;
}) {
  return (
    <div
      className={`flex flex-col gap-3 rounded-3xl border p-6 ${
        highlight
          ? "border-accent/30 bg-accent-soft"
          : "border-border bg-surface"
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-text-tertiary">
          {label}
        </p>
        <DeltaBadge pct={deltaPct} />
      </div>
      <div className="flex items-end justify-between gap-3">
        <p
          className={`font-semibold tracking-tight ${
            highlight ? "text-accent" : "text-text"
          }`}
          style={{ fontSize: "clamp(1.5rem, 2.4vw, 1.875rem)" }}
        >
          {value}
        </p>
        <Sparkline data={series} />
      </div>
    </div>
  );
}
